import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import blogPosts from '../data/blogPosts';
import Header from '../components/Header';

const BlogPost = ({ darkTheme, toggleTheme }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(false);

  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    // Reset loaded state when slug changes
    setLoaded(false);
  }, [slug]);

  if (!post) {
    return (
      <>
        <Header darkTheme={darkTheme} toggleTheme={toggleTheme} isBlogPage />
        <div style={{
          minHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--body-font)',
          color: 'var(--text-color)',
          gap: '1rem',
        }}>
          <i className="ri-error-warning-line" style={{ fontSize: '3rem', color: 'var(--first-color)' }}></i>
          <h2 style={{ color: 'var(--title-color)' }}>Post not found</h2>
          <button
            onClick={() => navigate('/blog')}
            className="button"
            style={{ marginTop: '0.5rem' }}
          >
            <i className="ri-arrow-left-line"></i>
            Back to Writing
          </button>
        </div>
      </>
    );
  }

  // The HTML file is served from /public/blogs/
  const src = `${process.env.PUBLIC_URL}/blogs/${post.filename}`;

  // Prev / next navigation
  const currentIndex = blogPosts.findIndex((p) => p.slug === slug);
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: 'var(--body-color)' }}>
      {/* Site navigation header */}
      <Header darkTheme={darkTheme} toggleTheme={toggleTheme} isBlogPage />

      {/* Thin top bar with back link + post nav */}
      <div style={{
        background: 'var(--container-color)',
        borderBottom: '1px solid var(--text-color-light)',
        transition: 'background-color 0.4s ease',
        zIndex: 50,
      }}>
        <div style={{
          maxWidth: 1120,
          margin: '0 auto',
          padding: '0.65rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap',
        }}>
          {/* Back */}
          <Link
            to="/blog"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              fontSize: 'var(--small-font-size)',
              fontWeight: 'var(--font-medium)',
              color: 'var(--text-color)',
              fontFamily: 'var(--body-font)',
              textDecoration: 'none',
              transition: 'color 0.3s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--first-color)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-color)')}
          >
            <i className="ri-arrow-left-line"></i>
            All Writing
          </Link>

          {/* Series label */}
          <span style={{
            fontSize: '0.72rem',
            fontWeight: 'var(--font-semi-bold)',
            color: 'var(--first-color)',
            background: `hsla(var(--first-color-hsl-h), var(--first-color-hsl-s), var(--first-color-hsl-l), 0.1)`,
            border: `1px solid hsla(var(--first-color-hsl-h), var(--first-color-hsl-s), var(--first-color-hsl-l), 0.25)`,
            padding: '0.25rem 0.7rem',
            borderRadius: 20,
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
            fontFamily: 'var(--body-font)',
          }}>
            {post.seriesTag}
          </span>

          {/* Prev / Next */}
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            {prevPost ? (
              <Link
                to={`/blog/${prevPost.slug}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  fontSize: 'var(--small-font-size)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--text-color)',
                  fontFamily: 'var(--body-font)',
                  textDecoration: 'none',
                  transition: 'color 0.3s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--first-color)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-color)')}
                title={prevPost.title}
              >
                <i className="ri-arrow-left-s-line"></i>
                Prev
              </Link>
            ) : (
              <span style={{ fontSize: 'var(--small-font-size)', color: 'var(--text-color-light)', fontFamily: 'var(--body-font)' }}>Prev</span>
            )}
            <span style={{ color: 'var(--text-color-light)', fontSize: 'var(--small-font-size)', fontFamily: 'var(--body-font)' }}>
              {currentIndex + 1} / {blogPosts.length}
            </span>
            {nextPost ? (
              <Link
                to={`/blog/${nextPost.slug}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  fontSize: 'var(--small-font-size)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--text-color)',
                  fontFamily: 'var(--body-font)',
                  textDecoration: 'none',
                  transition: 'color 0.3s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--first-color)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-color)')}
                title={nextPost.title}
              >
                Next
                <i className="ri-arrow-right-s-line"></i>
              </Link>
            ) : (
              <span style={{ fontSize: 'var(--small-font-size)', color: 'var(--text-color-light)', fontFamily: 'var(--body-font)' }}>Next</span>
            )}
          </div>
        </div>
      </div>

      {/* Loading skeleton */}
      {!loaded && (
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          flexDirection: 'column',
          gap: '1rem',
          fontFamily: 'var(--body-font)',
          color: 'var(--text-color)',
        }}>
          <i className="ri-loader-4-line" style={{ fontSize: '2rem', color: 'var(--first-color)', animation: 'spin 1s linear infinite' }}></i>
          <span style={{ fontSize: 'var(--small-font-size)' }}>Loading article…</span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* The blog post iframe */}
      <iframe
        key={slug}
        src={src}
        title={post.title}
        onLoad={() => setLoaded(true)}
        style={{
          flex: 1,
          width: '100%',
          border: 'none',
          display: loaded ? 'block' : 'none',
          minHeight: 'calc(100vh - 7rem)',
        }}
        allow="fullscreen"
      />
    </div>
  );
};

export default BlogPost;
